class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));

    // Quick remove when typing 0 or clearing input
    this.addEventListener('input', (event) => {
      if (!event.target.name?.includes('updates[')) return;

      const value = parseInt(event.target.value);
      if (value === 0 || event.target.value === '') {
        this.updateQuantity(
          event.target.dataset.index,
          0,
          event,
          event.target.getAttribute('name'),
          event.target.dataset.quantityVariantId
        );
      }
    });
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id, inputEl) {
    const cartItem =
      document.getElementById(`CartItem-${id}`) || document.getElementById(`CartDrawer-Item-${id}`);
    const inputs = cartItem
      ? cartItem.querySelectorAll('input.quantity__input')
      : inputEl
        ? [inputEl]
        : this.querySelectorAll(`#Quantity-${id}, #Drawer-quantity-${id}`);

    inputs.forEach((input) => {
      input.value = input.getAttribute('value');
    });
    this.isEnterPressed = false;
  }

  setLineItemError(line, message) {
    const cartItem =
      document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);

    if (cartItem) {
      cartItem.querySelectorAll('.cart-item__error-text').forEach((el) => {
        el.textContent = message;
      });
      return;
    }

    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;
  }

  setValidity(event, index, message) {
    this.setLineItemError(index, message);
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index, event.target);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    // Remove item if 0 entered
    if (inputValue === 0 || event.target.value === '') {
      this.updateQuantity(
        index,
        0,
        event,
        document.activeElement?.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
      return;
    }

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.setLineItemError(index, '');
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement?.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          if (typeof this.replaceCartDrawerContents === 'function') {
            this.replaceCartDrawerContents(responseText);
            return;
          }

          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    }

    const sectionId = document.getElementById('main-cart-items')?.dataset?.id || 'main-cart-items';

    return fetch(`${routes.cart_url}?section_id=${sectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        if (!sourceQty) return;

        this.className = sourceQty.className;
        this.innerHTML = sourceQty.innerHTML;

        if (typeof window.rushFeeSyncCartPageLayout === 'function') {
          window.rushFeeSyncCartPageLayout(sourceQty.classList.contains('is-empty'));
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items')?.dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '.js-contents-after',
      },
    ].filter(Boolean);
  }

  getSavedDiscountCode() {
    return sessionStorage.getItem('cart_discount_code') || null;
  }

  async reapplyDiscountCode(discountCode) {
    if (!discountCode) return;

    try {
      const response = await fetch(`${routes.cart_update_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: discountCode,
        }),
      });

      return response.ok;
    } catch (error) {
      console.error('Error reapplying discount code:', error);
      return false;
    }
  }

  renderParsedSections(parsedState) {
    if (typeof this.renderUpdatedSections === 'function') {
      this.renderUpdatedSections(parsedState);
      return;
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections?.[section.section];
      if (!sectionHtml) return;

      const elementToReplace =
        document.getElementById(section.id)?.querySelector(section.selector) ||
        document.getElementById(section.id);

      if (elementToReplace) {
        const inner = this.getSectionInnerHTML(sectionHtml, section.selector);
        if (inner) elementToReplace.innerHTML = inner;
      }
    });
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const requestedQuantity = parseInt(quantity, 10);

    const errorsEl = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
    if (errorsEl) errorsEl.textContent = '';

    const savedDiscountCode = this.getSavedDiscountCode();

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event?.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then(async (state) => {
        let parsedState = JSON.parse(state);

        if (typeof window.rushFeeRemoveIfOrphaned === 'function') {
          parsedState = await window.rushFeeRemoveIfOrphaned(parsedState, {
            skipRender: true,
            skipPublish: true,
          });
        }

        if (parsedState.item_count === 0) {
          if (typeof window.rushFeeSyncCartPageLayout === 'function') {
            window.rushFeeSyncCartPageLayout(true);
          } else if (typeof window.rushFeeSyncEmptyState === 'function') {
            window.rushFeeSyncEmptyState(0);
          }
        }

        const isCartDrawer = this.tagName === 'CART-DRAWER-ITEMS';
        let finalCartState = parsedState;
        let refreshSucceeded = false;

        if (parsedState.item_count > 0) {
          try {
            if (!isCartDrawer) {
              await new Promise((resolve) => setTimeout(resolve, 300));
            }

            const cartResponse = await fetch('/cart.js');
            const cartData = await cartResponse.json();

            if (savedDiscountCode) {
              await this.reapplyDiscountCode(savedDiscountCode);
              const reappliedCartResponse = await fetch('/cart.js');
              finalCartState = await reappliedCartResponse.json();
            } else {
              finalCartState = cartData;
            }

            if (!isCartDrawer) {
              const sectionsToRender = this.getSectionsToRender();
              const cartItemsSection = sectionsToRender.find((s) => s.id === 'main-cart-items');
              const footerSection = sectionsToRender.find((s) => s.id === 'main-cart-footer');

              const sectionIdCartItems = cartItemsSection?.section || 'main-cart-items';
              const updatedResponse = await fetch(`${routes.cart_url}?section_id=${sectionIdCartItems}`);
              const updatedText = await updatedResponse.text();
              const updatedState = new DOMParser().parseFromString(updatedText, 'text/html');

              const sourceQty = updatedState.querySelector('cart-items');
              if (sourceQty) {
                this.className = sourceQty.className;
                this.innerHTML = sourceQty.innerHTML;
              } else {
                const jsContents = updatedState.querySelector('#main-cart-items .js-contents, .js-contents');
                const targetContents = document.getElementById('main-cart-items')?.querySelector('.js-contents');
                if (jsContents && targetContents) targetContents.innerHTML = jsContents.innerHTML;
              }

              const sectionIdFooter = footerSection?.section || 'main-cart-footer';
              const footerResponse = await fetch(`${routes.cart_url}?section_id=${sectionIdFooter}`);
              const footerText = await footerResponse.text();
              const footerState = new DOMParser().parseFromString(footerText, 'text/html');

              const footerElement = document.getElementById('main-cart-footer')?.querySelector('.js-contents');
              const footerSource =
                footerState.querySelector('#main-cart-footer .js-contents') || footerState.querySelector('.js-contents');
              if (footerElement && footerSource) {
                footerElement.innerHTML = footerSource.innerHTML;
              }

              const footerAfterElement = document.getElementById('main-cart-footer')?.querySelector('.js-contents-after');
              const footerAfterSource = footerState.querySelector('#main-cart-footer .js-contents-after, .js-contents-after');
              if (footerAfterElement && footerAfterSource) {
                footerAfterElement.innerHTML = footerAfterSource.innerHTML;
              }
            }

            if (savedDiscountCode) {
              document.dispatchEvent(
                new CustomEvent('discount:reapplied', {
                  detail: { discountCode: savedDiscountCode, cart: finalCartState },
                })
              );
            }

            document.dispatchEvent(new CustomEvent('cart:updated', { detail: finalCartState, bubbles: true }));
            refreshSucceeded = !isCartDrawer;
          } catch (e) {
            console.warn('Cart section refresh failed, using initial response:', e);
            finalCartState = parsedState;
          }
        }

        if (!refreshSucceeded && parsedState.sections) {
          try {
            this.renderParsedSections(parsedState);
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: parsedState, bubbles: true }));
          } catch (fallbackErr) {
            console.warn('Fallback section update failed:', fallbackErr);
          }
        }

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const cartItemRow =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          const quantityElement =
            event?.target?.matches?.('input.quantity__input') && document.body.contains(event.target)
              ? event.target
              : cartItemRow?.querySelector('input.quantity__input') ||
                document.getElementById(`Quantity-${line}`) ||
                document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          const isCartEmpty = parsedState.item_count === 0;

          this.classList.toggle('is-empty', isCartEmpty);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', isCartEmpty);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', isCartEmpty);

          if (typeof window.rushFeeSyncCartPageLayout === 'function') {
            window.rushFeeSyncCartPageLayout(isCartEmpty);
          }

          if (!savedDiscountCode || parsedState.item_count === 0 || isCartDrawer) {
            this.renderParsedSections(parsedState);
          }

          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          const quantityMismatch =
            !Number.isNaN(requestedQuantity) &&
            typeof updatedValue !== 'undefined' &&
            updatedValue !== requestedQuantity;

          if (items.length === parsedState.items.length && quantityMismatch) {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          } else if (items.length !== parsedState.items.length && typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          }
          this.updateLiveRegions(line, message);

          const isCartDrawerQuantityUpdate =
            cartDrawerWrapper &&
            this.tagName === 'CART-DRAWER-ITEMS' &&
            (event?.target?.name?.includes('updates[') || event?.currentTarget instanceof CartRemoveButton);

          if (!isCartDrawerQuantityUpdate) {
            const lineItem =
              document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
            if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
              cartDrawerWrapper
                ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
                : lineItem.querySelector(`[name="${name}"]`).focus();
            } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
              trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
            } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
              trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
            }
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        if (parsedState.item_count === 0) {
          if (this.onCartUpdate) {
            await this.onCartUpdate();
          }

          const otherCart = isCartDrawer
            ? document.querySelector('cart-items')
            : document.querySelector('cart-drawer-items');
          if (otherCart?.onCartUpdate && otherCart !== this) {
            await otherCart.onCartUpdate();
          }

          document.dispatchEvent(new CustomEvent('cart:updated', { detail: parsedState, bubbles: true }));
        }

        if (typeof window.rushFeeUpdateCartBubbleCount === 'function') {
          window.rushFeeUpdateCartBubbleCount(parsedState);
        } else {
          this.updateCartIconBubble(parsedState);
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    this.setLineItemError(line, message);

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    if (cartStatus) {
      cartStatus.textContent = message;
      cartStatus.setAttribute('aria-hidden', false);

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', true);
      }, 1000);
    }
  }

  getSectionInnerHTML(html, selector) {
    if (!html || !selector) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  updateCartIconBubble(parsedState) {
    const cartIcon = document.getElementById('cart-icon-bubble');
    if (!cartIcon || !parsedState) return;

    let itemCount = parsedState.item_count;
    const bubbleSection = parsedState.sections?.['cart-icon-bubble'];

    if (bubbleSection) {
      const sectionInner = this.getSectionInnerHTML(bubbleSection, '.shopify-section');
      if (sectionInner) {
        cartIcon.innerHTML = sectionInner;
        if (itemCount == null) {
          const countSpan = cartIcon.querySelector('.cart-count-bubble [aria-hidden="true"]');
          itemCount = countSpan ? parseInt(countSpan.textContent, 10) : 0;
        }
        cartIcon.classList.toggle('header__icon_empty_icon', itemCount === 0);
        return;
      }
    }

    itemCount = itemCount ?? 0;

    cartIcon.classList.toggle('header__icon_empty_icon', itemCount === 0);

    const bubble = cartIcon.querySelector('.cart-count-bubble');
    if (itemCount === 0) {
      bubble?.remove();
      return;
    }

    if (bubble) {
      const countSpan = bubble.querySelector('[aria-hidden="true"]');
      if (countSpan) countSpan.textContent = itemCount;
      const srSpan = bubble.querySelector('.visually-hidden');
      if (srSpan) {
        srSpan.textContent = `${itemCount} item${itemCount === 1 ? '' : 's'}`;
      }
      return;
    }

    const iconWrapper = cartIcon.querySelector('.empty_icon_cart') || cartIcon;
    const newBubble = document.createElement('div');
    newBubble.className = 'cart-count-bubble';
    newBubble.innerHTML = `<span aria-hidden="true">${itemCount}</span><span class="visually-hidden">${itemCount} item${itemCount === 1 ? '' : 's'}</span>`;
    iconWrapper.appendChild(newBubble);
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    if (this.tagName === 'CART-DRAWER-ITEMS') {
      this.classList.add('is-updating');
      cartDrawerItemElements.forEach((overlay) => overlay.classList.remove('hidden'));
      this.lineItemStatusElement.setAttribute('aria-hidden', false);
      return;
    }

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    if (this.tagName === 'CART-DRAWER-ITEMS') {
      cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
      requestAnimationFrame(() => {
        this.classList.remove('is-updating');
      });
      return;
    }

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
